import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { ArrowLeft, AtSign, Loader2, MailCheck, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/lib/hooks/use-toast';
import { authService } from '../services/auth-service';
import { forgotPasswordSchema, ForgotPasswordFormData } from '../schemas/auth.schemas';
import { CAMPO_DE_INGRESO, ICONO_DE_CAMPO, ROTULO_DE_CAMPO } from './marco-de-ingreso';

/**
 * Pedir el enlace para una contraseña nueva. El servidor contesta lo mismo
 * exista o no el correo (no revela quién tiene cuenta), así que la pantalla
 * tampoco lo distingue.
 */
export function ForgotPasswordForm() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setIsLoading(true);
      await authService.forgotPassword(data);
      setEmailSent(true);
    } catch (error: any) {
      toast({
        title: 'No se pudo enviar el enlace',
        description: error.response?.data?.message || 'Prueba de nuevo en un momento.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const volver = (
    <Link
      to="/login"
      className="inline-flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="size-4" aria-hidden="true" />
      Volver a ingresar
    </Link>
  );

  if (emailSent) {
    return (
      <div className="flex flex-col items-center gap-6 text-center">
        <div className="flex size-16 items-center justify-center rounded-2xl bg-accent text-primary">
          <MailCheck className="size-8" aria-hidden="true" />
        </div>
        <div className="flex flex-col gap-2">
          <h2 className="font-titulo text-2xl font-semibold tracking-tight">Revisa tu correo</h2>
          <p className="text-[15px] leading-relaxed text-muted-foreground">
            Si <strong className="font-semibold text-foreground">{getValues('email')}</strong> tiene
            una cuenta en Prixia, te llegará un enlace para crear una contraseña nueva. Vence en una
            hora.
          </p>
        </div>
        <p className="text-sm text-muted-foreground">
          ¿No llegó? Revisa la carpeta de spam o{' '}
          <button
            type="button"
            onClick={() => setEmailSent(false)}
            className="font-medium text-primary hover:underline"
          >
            pide otro enlace
          </button>
          .
        </p>
        {volver}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h2 className="font-titulo text-[26px] font-semibold tracking-tight">
          ¿Olvidaste tu contraseña?
        </h2>
        <p className="text-[15px] text-muted-foreground">
          Escribe el correo con el que ingresas y te mandamos un enlace.
        </p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6" noValidate>
        <div className="flex flex-col gap-2.5">
          <Label htmlFor="email" className={ROTULO_DE_CAMPO}>
            Correo
          </Label>
          <div className="relative">
            <AtSign className={ICONO_DE_CAMPO} aria-hidden="true" />
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="nombre@empresa.com"
              {...register('email')}
              error={errors.email?.message}
              disabled={isLoading}
              className={CAMPO_DE_INGRESO}
            />
          </div>
        </div>
        <Button
          type="submit"
          variant="degradado"
          className="h-[52px] w-full rounded-xl text-base"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="size-[18px] animate-spin" aria-hidden="true" />
              Enviando…
            </>
          ) : (
            <>
              Enviarme el enlace
              <Send className="size-[18px]" aria-hidden="true" />
            </>
          )}
        </Button>
        {volver}
      </form>
    </div>
  );
}

export default ForgotPasswordForm;
